import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Outlet } from 'react-router-dom';
import Link from 'next/link';
import { Shield, Users, BarChart3, Loader2, ArrowLeft, Trophy, Zap, Gift, Compass } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';

const ADMIN_NAV = [
  { href: '/admin', label: 'Dashboard', icon: BarChart3 },
  { href: '/admin/main-events', label: 'Main Events', icon: Trophy },
  { href: '/admin/users', label: 'Users', icon: Users },
  { href: '/admin/prop-boost', label: 'PropBoost', icon: Zap },
  { href: '/admin/rewards', label: 'Rewards', icon: Gift },
  { href: '/admin/quests', label: 'Quests', icon: Compass },
];

// React Router renders matched child routes via <Outlet/> instead of taking
// `children`. The original Next.js layout accepted `children: ReactNode`.
export default function AdminLayout() {
  const router = useRouter();
  const pathname = usePathname();
  const { user, isAuthenticated, isLoading, isHydrated } = useAuthStore();
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    if (!isHydrated || isLoading) return;

    if (!isAuthenticated) {
      router.push('/login?redirect=/admin');
      return;
    }

    if (user?.role !== 'ADMIN') {
      router.push('/?error=unauthorized');
      return;
    }

    setIsAuthorized(true);
  }, [isHydrated, isLoading, isAuthenticated, user, router]);

  if (!isHydrated || isLoading || !isAuthorized) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Admin Header */}
      <div className="bg-gradient-to-r from-red-900/50 via-zinc-900 to-zinc-900 border-b border-red-900/50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-8 w-8 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <Shield className="h-4 w-4 text-red-400" />
              </div>
              <div className="min-w-0">
                <h1 className="font-bold text-white truncate">Admin Panel</h1>
                <p className="text-xs text-zinc-400 truncate">FantasyPRIME NBA</p>
              </div>
            </div>
            <Link
              href="/"
              className="flex items-center gap-1.5 sm:gap-2 text-sm text-zinc-400 hover:text-white transition-colors flex-shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Site</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Admin Navigation */}
      <div className="border-b border-zinc-800 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 py-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            {ADMIN_NAV.map((item) => {
              const Icon = item.icon;
              const isActive = pathname === item.href ||
                (item.href !== '/admin' && pathname.startsWith(item.href));

              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    'flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0',
                    isActive
                      ? 'bg-red-500/10 text-red-400'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
        <Outlet />
      </div>
    </div>
  );
}
